import { Col, SegmentControl, Sidepanel, Spinner2 } from 'components';
import { theme } from 'lib';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ContentHeader } from 'screens/Admin/components';
import {
  FanChatAccessPanel,
  GloryBoardPanel,
  Settings,
  Team,
  TeamAudiencePanel,
  TeamDetail
} from './components';

const TABS = ['Details', 'Followers', 'Subscribers', 'GloryBoard', 'FanChat'];

// The tab is `?tab=` rather than component state so a link carries it. `details` is the absent
// value: an address with no tab opens on Details, which is where every freshly opened panel starts.
const TAB_KEYS = ['details', 'followers', 'subscribers', 'gloryboard', 'fanchat'];

const TeamPanel = ({ dataId, closeModal, hasBack }) => {
  const [data, setData] = useState(null),
    [showRemoveAlert, setShowRemoveAlert] = useState(false),
    [isEditMode, setIsEditMode] = useState(false),
    [searchParams, setSearchParams] = useSearchParams(),
    storedData = useSelector((state) => state.admin?.teams?.find((x) => x._id === dataId));

  const isCreating = dataId === true;
  const isViewing = !isCreating && !isEditMode;

  const tabIdx = Math.max(0, TAB_KEYS.indexOf(searchParams.get('tab')));

  // Leaving the GloryBoard also drops the fan selected on it — that selection means nothing under
  // the Details tab, and carrying it in the address would resurrect it on the next visit.
  const setTabIdx = (idx) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        if (idx === 0) {
          next.delete('tab');
          next.delete('participant');
        } else {
          next.set('tab', TAB_KEYS[idx]);
        }

        return next;
      },
      { replace: true }
    );

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleSaveEdit = () => {
    setIsEditMode(false);
  };

  return (
    <React.Fragment>
      <Sidepanel wid={1000} pad="0" visible={dataId} closeModal={closeModal}>
        <ContentHeader
          text={
            isCreating
              ? 'Create new team'
              : isEditMode
                ? `Edit ${storedData?.name || storedData?.username || 'Team'}`
                : storedData?.name || storedData?.username || 'Team Details'
          }
          SettingsComponent={() =>
            !isEditMode && <Settings team={storedData} setShowRemoveAlert={setShowRemoveAlert} />
          }
          closePanel={hasBack && closeModal}
        />

        {dataId && (typeof dataId === 'string' ? !storedData : false) && (
          <Col
            zIndex={5}
            pos="absolute"
            ht="100%"
            wid="100%"
            bg="rgba(210, 216, 223, 0.7)"
            left="0"
            centerAll
          >
            <Spinner2 col={theme.SECONDARY} />
          </Col>
        )}

        {dataId && (typeof dataId === 'string' ? storedData : true) && (
          <>
            {isCreating || isEditMode ? (
              <Team
                team={storedData}
                closeModal={isCreating ? closeModal : handleCancelEdit}
                setShowRemoveAlert={setShowRemoveAlert}
                showRemoveAlert={showRemoveAlert}
                onSave={handleSaveEdit}
              />
            ) : (
              <>
                {isViewing && (
                  <Col noFlex pad="24px 24px 0">
                    <SegmentControl
                      options={TABS}
                      selectedIdx={tabIdx}
                      handleSelect={setTabIdx}
                      wid="560px"
                      small
                    />
                  </Col>
                )}

                {TAB_KEYS[tabIdx] === 'details' && (
                  <TeamDetail team={storedData} onEdit={handleEdit} />
                )}
                {(TAB_KEYS[tabIdx] === 'followers' || TAB_KEYS[tabIdx] === 'subscribers') && (
                  <TeamAudiencePanel team={storedData} kind={TAB_KEYS[tabIdx]} />
                )}
                {TAB_KEYS[tabIdx] === 'gloryboard' && <GloryBoardPanel team={storedData} />}
                {TAB_KEYS[tabIdx] === 'fanchat' && <FanChatAccessPanel team={storedData} />}
              </>
            )}
          </>
        )}
      </Sidepanel>
    </React.Fragment>
  );
};

export default TeamPanel;
